import { useCallback, useMemo } from 'react'

import { Card, DescriptionList, SkeletonBox } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { useModal } from '@circlefin/modal-router'
import { paymentMethodsSection } from '@services/sections/lib/billing'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import {
  CardPaymentMethodsDocument,
  LegalEntity,
  useCardPaymentMethodByIdQuery,
  useEntityQuery,
  useRemoveMerchantCreditCardMutation,
  useUpdateCardPaymentMethodMutation,
} from '@shared/graphql'
import { useMfa } from '@shared/mfa'
import { AddressElement } from '@shared/stripe'
import {
  AddressElement as StripeAddressElement,
  useElements,
} from '@stripe/react-stripe-js'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import * as Buttons from '../../../buttons'

import type { SubmitHandler } from '@circlefin/form/useForm'

const { Label, Description } = DescriptionList

const schema = y.object({
  /**
   * Set as the default credit card.
   */
  isDefault: y.boolean().required(),
})

type EditCardPaymentMethodSchema = y.InferType<typeof schema>

export interface EditCardPaymentMethodProps {
  /**
   * Payment method id.
   */
  cardId: string
}

export const EditCardPaymentMethod = ({
  cardId,
}: EditCardPaymentMethodProps) => {
  const { t } = useTranslation('settings')
  const [withMfa, { context, mfaError, mfaSuccess }] = useMfa()
  const modal = useModal()
  const elements = useElements()
  const router = useRouter()
  const { data: entityData } = useEntityQuery()
  const { data, error, refetch, loading } = useCardPaymentMethodByIdQuery({
    nextFetchPolicy: 'cache-and-network',
    variables: {
      id: cardId,
    },
    onCompleted: (data) => {
      const isDefault = data.cardPaymentMethodById.isDefault
      if (isDefault) {
        setValue('isDefault', isDefault)
      }
    },
  })
  const [updateCardPaymentMethod] = useUpdateCardPaymentMethodMutation({
    refetchQueries: [CardPaymentMethodsDocument],
    awaitRefetchQueries: true,
    onCompleted: () => {
      mfaSuccess({
        message: t`common:success`,
      })
      void router.push(paymentMethodsSection.route)
    },
    onError: (error) => {
      mfaError({ error })
    },
  })

  const cardDetails = data?.cardPaymentMethodById

  const [Form, { setValue }] = useForm<EditCardPaymentMethodSchema>({
    schema,
    defaultValues: {
      isDefault: false,
    },
  })

  const addressDefaultValues = useMemo(
    () => ({
      name: cardDetails?.billingDetails.name ?? '',
      address: {
        line1: cardDetails?.billingDetails.line1 ?? '',
        line2: cardDetails?.billingDetails.line2 ?? '',
        city: cardDetails?.billingDetails.city ?? '',
        state: cardDetails?.billingDetails.district ?? '',
        postal_code: cardDetails?.billingDetails.postalCode ?? '',
        country: cardDetails?.billingDetails.country ?? '',
      },
    }),
    [cardDetails],
  )

  const [removeCard] = useRemoveMerchantCreditCardMutation({
    refetchQueries: [CardPaymentMethodsDocument],
    awaitRefetchQueries: true,
    onCompleted: () => {
      mfaSuccess({
        message: t`common:success`,
        description: cardDetails?.isDefault
          ? t`invoices.billing.linkedCards.deleteDefaultCard`
          : t`invoices.billing.linkedCards.deleteCard`,
      })
      void router.push(paymentMethodsSection.route)
    },
    onError: (error) => {
      mfaError({ error })
    },
  })

  const handleRemoveCard = useCallback(() => {
    if (cardDetails) {
      withMfa({
        onComplete: (code) => {
          void removeCard({
            variables: {
              id: cardDetails.id,
            },
            context: context(code),
          })
        },
        onAbort: () => modal.close({ context: 'onDismiss' }),
      })
    }
  }, [withMfa, context, cardDetails, removeCard, modal])

  const onSubmit: SubmitHandler<EditCardPaymentMethodSchema> = useCallback(
    async (values) => {
      const addressElement = elements?.getElement(StripeAddressElement)

      // do nothing if elements not rendered or if cardData does not exist
      if (!addressElement || !cardDetails) {
        return
      }

      // getValue triggers validation
      const {
        value: { address, name },
        complete,
      } = await addressElement.getValue()

      if (!complete) {
        return
      }

      withMfa({
        onComplete: (code) => {
          void updateCardPaymentMethod({
            variables: {
              updateCardPaymentMethodInput: {
                billingDetails: {
                  name: name,
                  line1: address.line1,
                  line2: address.line2 || '',
                  city: address.city,
                  district: address.state,
                  postalCode: address.postal_code,
                  country: address.country,
                },
              },
              cardId: cardDetails.id,
              isDefault: values.isDefault,
            },
            context: context(code),
          })
        },
        onAbort: () => modal.close({ context: 'onDismiss' }),
      })
    },
    [cardDetails, context, elements, modal, updateCardPaymentMethod, withMfa],
  )
  return (
    <GraphQLErrorBoundary error={error} retry={refetch}>
      <Form className="space-y-8" onSubmit={onSubmit}>
        <Card>
          <Card.Content className="pb-8 pl-8 pt-5">
            <div
              className=" px-4 pb-7 pt-4 text-lg leading-6 font-circular-book"
              data-testid="card-details-title"
            >
              {t('billing.cardPayment.details.title')}
            </div>
            <DescriptionList className="ml-4 grid-cols-2">
              <Label className="w-64">
                {t('billing.cardPayment.details.cardNumber.label')}
              </Label>
              <Description>
                <SkeletonBox className="my-1 h-3 w-56" loading={loading}>
                  {t('billing.cardPayment.details.cardNumber.value', {
                    network: cardDetails?.network ?? '',
                    last4: cardDetails?.last4 ?? '',
                  })}
                </SkeletonBox>
              </Description>
              <Label className="w-64">
                {t('billing.cardPayment.details.cvv')}
              </Label>
              <Description>
                <SkeletonBox className="my-1 h-3 w-56" loading={loading}>
                  ***
                </SkeletonBox>
              </Description>
              <Label className="w-64">
                {t('billing.cardPayment.details.expiry')}
              </Label>
              <Description>
                <SkeletonBox className="my-1 h-3 w-56" loading={loading}>
                  {cardDetails
                    ? `${cardDetails.expMonth}/${cardDetails.expYear}`
                    : ''}
                </SkeletonBox>
              </Description>
              <Label className="w-64">
                {t('billing.cardPayment.details.setDefault')}
              </Label>
              <Description>
                <SkeletonBox className="my-1 h-3 w-56" loading={loading}>
                  <Form.Checkbox
                    disabled={
                      entityData?.entity?.legalEntity !== LegalEntity.CIRCLE_INC
                    }
                    name="isDefault"
                  />
                </SkeletonBox>
              </Description>
            </DescriptionList>
          </Card.Content>
        </Card>
        <Card>
          <Card.Content className="pb-8 pl-8 pt-5">
            <div
              className="px-4 pb-7 pt-4 text-lg leading-6 font-circular-book"
              data-testid="card-holderDetails-title"
            >
              {t('billing.cardPayment.holderDetails.title')}
            </div>
            <SkeletonBox className="h-8 w-full px-4" loading={loading}>
              <AddressElement
                className="px-4"
                options={{ defaultValues: addressDefaultValues }}
              />
            </SkeletonBox>
          </Card.Content>
        </Card>
        {!error &&
          entityData?.entity?.legalEntity === LegalEntity.CIRCLE_INC && (
            <div className="flex justify-end space-x-8 p-6">
              <Buttons.BillingDashboard
                className="w-28"
                onClick={handleRemoveCard}
                variant="text"
              >
                {t('common:delete')}
              </Buttons.BillingDashboard>
              <Form.SubmitButton className="w-28" variant="primary">
                {t('common:update')}
              </Form.SubmitButton>
            </div>
          )}
      </Form>
    </GraphQLErrorBoundary>
  )
}
